import React, { useEffect, useState } from "react";
import styled from "styled-components";
import axios from "../services/axios";
import Header from "../components/Header";
import { useThemeContext } from "../context/ThemeContext";
import { useUser } from "../context/UserContext";

const PageWrapper = styled.div`
  padding: 40px 20px;
  background: ${({ theme }) => theme.background || "#f9f9f9"};
  min-height: 100vh;
  font-family: ${({ theme }) => theme.font || "sans-serif"};
`;

const Panel = styled.div`
  padding: 2rem;
  background: ${({ theme }) => theme.card || "#fff"};
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  max-width: 900px;
  margin: 0 auto;
`;

const Section = styled.section`
  margin-bottom: 2rem;
  padding: 1.25rem;
  background: ${({ theme }) => theme.section || "#f0f0f0"};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border || "#ccc"};
`;

const SubHeading = styled.h3`
  color: ${({ theme }) => theme.text || "#333"};
  margin-bottom: 1rem;
  font-size: 1.1rem;
`;

const Button = styled.button`
  padding: 8px 16px;
  background-color: ${({ theme }) => theme.primary || "#007bff"};
  color: ${({ theme }) => theme.buttonText || "white"};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  min-width: 140px;

  &:hover {
    background-color: ${({ theme }) => theme.primaryHover || "#0069d9"};
  }
`;

const ErrorText = styled.p`
  color: #b00020;
  font-size: 0.9rem;
  margin-top: 0.5rem;
`;

const SuccessText = styled.p`
  color: green;
  font-size: 0.9rem;
  margin-top: 0.5rem;
`;

const BlogRow = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 12px;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid ${({ theme }) => theme.borderLight || "#ddd"};
`;

const CheckGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 18px;
  align-items: center;
`;

const CheckLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
`;

const Muted = styled.p`
  color: #666;
  margin: 0.25rem 0 0;
`;

const ProfilePage = () => {
  const { theme } = useThemeContext();
  const { user } = useUser();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const fetchRows = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get("/api/users/me/notifications/");
      setRows(res.data?.blogs || []);
    } catch (err) {
      setError("Failed to load notification settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const sendResetEmail = async () => {
    setError("");
    setSuccess("");
    try {
      await axios.post("/api/password-reset/", { email: user?.email });
      setSuccess("Password reset email sent.");
    } catch {
      setError("Failed to send password reset email.");
    }
  };

  const effectiveValue = (row, key) => {
    if (row?.watch && typeof row.watch[key] === "boolean") return row.watch[key];
    return row?.defaults?.[key];
  };

  const updateWatch = async (blogId, patch) => {
    setError("");
    setSuccess("");
    try {
      const res = await axios.patch("/api/users/me/notifications/", {
        blog_id: blogId,
        ...patch,
      });
      setRows((prev) =>
        prev.map((r) =>
          r.blog_id === blogId
            ? { ...r, watch: res.data?.watch || r.watch }
            : r
        )
      );
      setSuccess("Notification preferences updated.");
    } catch {
      setError("Failed to update notifications.");
    }
  };

  return (
    <>
      <Header />
      <PageWrapper theme={theme}>
        <Panel theme={theme}>
          <Section theme={theme}>
            <SubHeading theme={theme}>Profile</SubHeading>
            <div>
              <div><strong>Username:</strong> {user?.username}</div>
              <div><strong>Email:</strong> {user?.email}</div>
            </div>
            <div style={{ marginTop: 12 }}>
              <Button theme={theme} type="button" onClick={sendResetEmail}>
                Send Password Reset Email
              </Button>
            </div>
          </Section>

          <Section theme={theme}>
            <SubHeading theme={theme}>Blog Notifications</SubHeading>
            {loading && <Muted>Loading...</Muted>}
            {!loading && rows.length === 0 && (
              <Muted>No blogs found for this user.</Muted>
            )}

            {rows.map((row) => {
              const notificationsEnabled = effectiveValue(row, "notifications_enabled");
              const canEdit = row.role !== "viewer";

              return (
                <BlogRow key={row.blog_id} theme={theme}>
                  <div>
                    <div style={{ fontWeight: 700 }}>{row.blog_title}</div>
                    <Muted>Role: {row.role}</Muted>
                    {!notificationsEnabled && (
                      <Muted>Notifications disabled by blog admin.</Muted>
                    )}
                  </div>

                  {canEdit ? (
                    <CheckGroup>
                      <CheckLabel>
                        <input
                          type="checkbox"
                          checked={!!effectiveValue(row, "enabled")}
                          disabled={!notificationsEnabled}
                          onChange={(e) =>
                            updateWatch(row.blog_id, { enabled: e.target.checked })
                          }
                        />
                        Enabled
                      </CheckLabel>
                      <CheckLabel>
                        <input
                          type="checkbox"
                          checked={!!effectiveValue(row, "notify_new_post")}
                          disabled={!notificationsEnabled}
                          onChange={(e) =>
                            updateWatch(row.blog_id, { notify_new_post: e.target.checked })
                          }
                        />
                        New Post
                      </CheckLabel>
                      <CheckLabel>
                        <input
                          type="checkbox"
                          checked={!!effectiveValue(row, "notify_new_moment")}
                          disabled={!notificationsEnabled}
                          onChange={(e) =>
                            updateWatch(row.blog_id, { notify_new_moment: e.target.checked })
                          }
                        />
                        New Moment
                      </CheckLabel>
                      <CheckLabel>
                        <input
                          type="checkbox"
                          checked={!!effectiveValue(row, "notify_new_reaction")}
                          disabled={!notificationsEnabled}
                          onChange={(e) =>
                            updateWatch(row.blog_id, { notify_new_reaction: e.target.checked })
                          }
                        />
                        New Reaction
                      </CheckLabel>
                      <CheckLabel>
                        <input
                          type="checkbox"
                          checked={!!effectiveValue(row, "notify_new_comment")}
                          disabled={!notificationsEnabled}
                          onChange={(e) =>
                            updateWatch(row.blog_id, { notify_new_comment: e.target.checked })
                          }
                        />
                        New Comment
                      </CheckLabel>
                    </CheckGroup>
                  ) : (
                    <Muted>
                      Notification controls are currently disabled for viewers.
                    </Muted>
                  )}

                  {/*
                  <CheckGroup>
                    Viewer self-service toggles can be enabled here when ready.
                  </CheckGroup>
                  */}
                </BlogRow>
              );
            })}
          </Section>

          {error && <ErrorText>{error}</ErrorText>}
          {success && <SuccessText>{success}</SuccessText>}
        </Panel>
      </PageWrapper>
    </>
  );
};

export default ProfilePage;
